import {
  KeyboardEvent,
  PointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import { HEALTH_COLORS } from './constants';

import { PoolRow } from '@/services/preferredPoolsAPI';
import styled from '@/styles';

// ui-prototype/preferred-worker-pool/client-detail.html depth card
// (#poolGaugeArc/#poolGaugeFrac/#poolTitle/#poolDesc/#poolStatusPill). The
// vertical gauge doubles as the OQ-10 alert-threshold slider: dragging
// #poolVgaugeMarker (or arrow keys on it) picks a new alert_threshold and
// commits it via PATCH on release — the plan's approved v1 threshold editing.

const GAUGE_CIRCUMFERENCE = 263.8; // r=42, per the prototype SVG
const VGAUGE_HEIGHT = 60;
// A threshold of 0 means "alert when the pool drops to zero" — unreachable, so
// the alarm is off while the card still reads as if it's armed. 1 is the
// lowest value that can actually fire.
const MIN_ALERT_THRESHOLD = 1;
// Long enough to collapse a held arrow key / rapid taps into one write, short
// enough that a deliberate single press still feels immediate.
const KEYBOARD_COMMIT_DELAY_MS = 500;

const CardWrap = styled('div', {
  background: '#FCFCFC',
  border: '1px solid #DADADA',
  borderRadius: 8,
  padding: 16,
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});

const Gauge = styled('div', {
  width: 100,
  height: 100,
  flexShrink: 0,
  position: 'relative',
});

const GaugeFrac = styled('div', {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '18px',
  fontWeight: 800,
  color: '#1C1C1C',
});

const TextCol = styled('div', {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const Title = styled('div', {
  fontSize: '16px',
  fontWeight: 700,
  color: '#1C1C1C',
});

const Desc = styled('div', {
  fontSize: '14px',
  color: '#1C1C1C',
  lineHeight: 1.4,
});

const StatusPill = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  background: '#E7F4E6',
  color: '#2E8722',
  borderRadius: 8,
  padding: '4px 8px',
  fontSize: '11px',
  fontWeight: 600,
  alignSelf: 'flex-start',
});

const VgaugeWrap = styled('div', {
  width: 4,
  height: VGAUGE_HEIGHT,
  position: 'relative',
  flexShrink: 0,
  cursor: 'ns-resize',
});

const Vgauge = styled('div', {
  width: 4,
  height: VGAUGE_HEIGHT,
  borderRadius: 100,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

const Marker = styled('div', {
  position: 'absolute',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 18,
  height: 5,
  background: '#FCFCFC',
  border: '0.4px solid #DADADA',
  borderRadius: 100,
  boxShadow: '0 1px 2px rgba(0,0,0,0.24)',
  cursor: 'grab',
  touchAction: 'none',
});

const CapRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: '12px',
  color: '#6B7280',
});

const CapButton = styled('button', {
  background: 'none',
  border: 'none',
  padding: 0,
  color: '#2E8722',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  '&:disabled': { color: '#9CA3AF', cursor: 'default' },
});

const CapInput = styled('input', {
  width: 64,
  padding: '2px 6px',
  fontSize: '12px',
  border: '1px solid #D1D5DB',
  borderRadius: 4,
});

type Props = {
  pool: PoolRow;
  onThresholdChange?: (alertThreshold: number) => void;
  // GRAVY-10943 FP-3.6 (Product, 2026-10-21): ops change a pool's hard cap
  // here. Passed only while the cap is in force (pools follow Custom
  // Rates); the server refuses a cap below the target.
  onMaxMembersChange?: (maxMembers: number) => void;
};

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const thresholdToTop = (threshold: number, target: number) =>
  clamp(
    VGAUGE_HEIGHT - (threshold / target) * VGAUGE_HEIGHT,
    4,
    VGAUGE_HEIGHT - 4,
  );

const MaxMembersEditor = ({
  pool,
  onChange,
}: {
  pool: PoolRow;
  onChange: (maxMembers: number) => void;
}) => {
  const [draft, setDraft] = useState<string | null>(null);
  const cap = pool.max_members ?? 0;
  const value = Number(draft);
  const valid =
    draft !== null && Number.isInteger(value) && value >= pool.target_depth;

  if (draft === null) {
    return (
      <CapRow data-testid="poolCap">
        <span>
          Cap: {cap} member{cap === 1 ? '' : 's'}
        </span>
        <CapButton
          data-testid="poolCapEdit"
          type="button"
          onClick={() => setDraft(String(cap))}
        >
          Change
        </CapButton>
      </CapRow>
    );
  }
  return (
    <CapRow data-testid="poolCap">
      <label htmlFor={`pool-cap-${pool.id}`}>Cap</label>
      <CapInput
        data-testid="poolCapInput"
        id={`pool-cap-${pool.id}`}
        min={pool.target_depth}
        type="number"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
      <CapButton
        data-testid="poolCapSave"
        disabled={!valid}
        type="button"
        onClick={() => {
          if (value !== cap) onChange(value);
          setDraft(null);
        }}
      >
        Save
      </CapButton>
      <CapButton type="button" onClick={() => setDraft(null)}>
        Cancel
      </CapButton>
      {!valid && <span>At least {pool.target_depth} (the target)</span>}
    </CapRow>
  );
};

const PoolDepthCard = ({
  pool,
  onThresholdChange,
  onMaxMembersChange,
}: Props) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  // Set only by a pointerdown on the MARKER, so a click on the surrounding
  // gauge track no longer commits a threshold (see handlePointerDown).
  const draggingRef = useRef(false);
  const [draftThreshold, setDraftThreshold] = useState<number | null>(null);
  const threshold = draftThreshold ?? pool.alert_threshold;

  useEffect(() => {
    setDraftThreshold(null);
  }, [pool.id, pool.alert_threshold]);

  const filled =
    clamp(pool.active_member_count / pool.target_depth, 0, 1) *
    GAUGE_CIRCUMFERENCE;
  const color = HEALTH_COLORS[pool.health];
  const count = pool.active_member_count;

  const thresholdFromPointer = (event: PointerEvent<HTMLDivElement>) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect || rect.height === 0) return threshold;
    const fraction = 1 - (event.clientY - rect.top) / rect.height;
    return clamp(
      Math.round(fraction * pool.target_depth),
      MIN_ALERT_THRESHOLD,
      pool.target_depth,
    );
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!onThresholdChange) return;
    // Drag only. A plain CLICK used to set the draft here and commit it on
    // pointerup, so one stray click on a 4px-wide target silently rewrote the
    // client's alert threshold with no confirm and no undo. Arm on the MARKER
    // (see draggingRef) instead of anywhere on the gauge.
    if (!draggingRef.current) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDraftThreshold(thresholdFromPointer(event));
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (draftThreshold === null) return;
    setDraftThreshold(thresholdFromPointer(event));
  };

  const handlePointerUp = () => {
    draggingRef.current = false;
    if (draftThreshold === null) return;
    if (draftThreshold !== pool.alert_threshold) {
      onThresholdChange?.(draftThreshold);
    }
    setDraftThreshold(null);
  };

  // Keyboard commits are DEBOUNCED and accumulate on local draft state.
  //
  // Two bugs, one cause: this used to derive `next` from `pool.alert_threshold`
  // — a prop that only refreshes after loadPools() completes a round-trip — and
  // call onThresholdChange on every keydown. So (a) every press inside one
  // round-trip computed the SAME value, and 5 ArrowUps from 1 landed on 2
  // rather than 6, and (b) each press was its own PATCH, so holding the key
  // was a write storm. Reading `threshold` (draft ?? prop) makes presses
  // accumulate; the timer collapses a burst into one write on settle.
  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleCommit = (next: number) => {
    if (commitTimer.current) clearTimeout(commitTimer.current);
    commitTimer.current = setTimeout(() => {
      commitTimer.current = null;
      setDraftThreshold((draft) => {
        if (draft !== null && draft !== pool.alert_threshold) {
          onThresholdChange?.(draft);
        }
        // Keep showing the draft until the refreshed pool prop arrives —
        // clearing it here would snap the marker back to the stale value for
        // the length of the round-trip.
        return draft;
      });
    }, KEYBOARD_COMMIT_DELAY_MS);
    setDraftThreshold(next);
  };

  useEffect(
    () => () => {
      if (commitTimer.current) clearTimeout(commitTimer.current);
    },
    [],
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onThresholdChange) return;
    let next: number | null = null;
    // MIN_ALERT_THRESHOLD, not 0: an alert that fires when depth drops to 0 can
    // never fire, so 0 silently disabled the pool alarm while the card still
    // read like it was armed.
    if (event.key === 'ArrowUp')
      next = clamp(threshold + 1, MIN_ALERT_THRESHOLD, pool.target_depth);
    if (event.key === 'ArrowDown')
      next = clamp(threshold - 1, MIN_ALERT_THRESHOLD, pool.target_depth);
    if (next === null) return;
    event.preventDefault();
    if (next !== threshold) scheduleCommit(next);
  };

  return (
    <CardWrap>
      <Gauge>
        <svg viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            fill="none"
            r="42"
            stroke="#EAEAEA"
            strokeWidth="8"
          />
          <circle
            cx="50"
            cy="50"
            data-testid="poolGaugeArc"
            fill="none"
            r="42"
            stroke={color}
            strokeDasharray={`${filled.toFixed(1)} ${GAUGE_CIRCUMFERENCE}`}
            strokeLinecap="round"
            strokeWidth="8"
            transform="rotate(-90 50 50)"
          />
        </svg>
        <GaugeFrac data-testid="poolGaugeFrac">
          {count}/{pool.target_depth}
        </GaugeFrac>
      </Gauge>
      <TextCol>
        <Title data-testid="poolTitle">
          {pool.skill.name} pool depth: {count} active worker
          {count === 1 ? '' : 's'}
        </Title>
        <Desc data-testid="poolDesc">
          This pool is kept at {pool.target_depth}+ workers so a single
          cancellation won&rsquo;t leave a shift unfilled &mdash; you&rsquo;ll
          be alerted if it drops to {threshold}.
        </Desc>
        {onMaxMembersChange && pool.max_members != null && (
          <MaxMembersEditor pool={pool} onChange={onMaxMembersChange} />
        )}
        {pool.rookie_slots_filled > 0 && (
          <StatusPill data-testid="poolStatusPill">
            {pool.rookie_slots_filled} rookie slot
            {pool.rookie_slots_filled === 1 ? '' : 's'} filled
          </StatusPill>
        )}
      </TextCol>
      <VgaugeWrap
        ref={wrapRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <Vgauge>
          <div style={{ background: '#44A735', flex: '0 0 32px' }} />
          <div style={{ background: '#ECA300', flex: 1 }} />
          <div style={{ background: '#CF040C', flex: 1 }} />
        </Vgauge>
        <Marker
          aria-label="Alert threshold"
          aria-orientation="vertical"
          aria-valuemax={pool.target_depth}
          aria-valuemin={MIN_ALERT_THRESHOLD}
          aria-valuenow={threshold}
          data-testid="poolVgaugeMarker"
          role="slider"
          style={{ top: thresholdToTop(threshold, pool.target_depth) }}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          // Arms the drag. Only a press that STARTS on the marker can move the
          // threshold, so clicking the track is inert.
          onPointerDown={() => {
            draggingRef.current = true;
          }}
        />
      </VgaugeWrap>
    </CardWrap>
  );
};

export default PoolDepthCard;
